import _, { result } from "lodash";
import { SetStateAction, useEffect, useState, Dispatch, useRef } from "react";
import { restRequest } from "../http";
import { IssueOverview } from "../models/issue_overview_model";
import { on } from "events";

// export interface ModalControlData<D=undefined> { props: { open: boolean, onClose: () => void }, setOpen: React.Dispatch<React.SetStateAction<boolean>>, data?: D, setData: Dispatch<SetStateAction<D | undefined>> };


type OnDone<D> = (data: D) => boolean;
export class ModalControlData<D = undefined> {
    props: { open: boolean; onClose: () => void; };
    setOpen: Dispatch<SetStateAction<boolean>>;
    private _data: D;
    private _setData: Dispatch<SetStateAction<D>>;
    private onDone?: OnDone<D>;
    public constructor(props: { open: boolean, onClose: () => void }, setOpen: React.Dispatch<React.SetStateAction<boolean>>, data: D, setData: Dispatch<SetStateAction<D>>) {
        this.props = props;
        this.setOpen = setOpen;
        this._data = data;
        this._setData = setData;
    }

    public get data() {
        return this._data;
    }

    public set data(val: D) {
        this._setData(val);
    }

    update(open: boolean, data: D) {
        this.props.open = open;
        this._data = data;
    }

    public start(data: D, done: OnDone<D>) {
        this._setData(data);
        this.onDone = done;
        this.setOpen(true);
    }

    public finish(data: D) {
        if (this.onDone) {
            if (this.onDone(data)) {
                this._setData(data);
                this.onDone = undefined
                this.setOpen(false);
            }
        } else {
            this.setOpen(false);
        }
    }

};

export function useModalControl<D>(initialData: D): ModalControlData<D>;
export function useModalControl<D = undefined>(): ModalControlData<D | undefined>;

export function useModalControl<D>(initialData?: D): ModalControlData<D | undefined> {
    const [open, setOpen] = useState<boolean>(false);
    const [data, setData] = useState(initialData);
    const [reflush, setReflush] = useState(0);
    const controlRef = useRef<ModalControlData<D | undefined>>();

    useEffect(() => {
        if (controlRef.current) {
            controlRef.current.update(open, data);

            setReflush(() => reflush + 1);
        }
    }, [open, data]);

    if (!controlRef.current) {
        controlRef.current = new ModalControlData({ open, onClose: () => setOpen(false) }, setOpen, data, setData);
    }

    return controlRef.current;
}


export function useRequest<D>(path: string, body: any | undefined = undefined, method: 'post' | 'get' | 'delete' | 'put' = 'get'): [D | undefined, boolean, Dispatch<SetStateAction<any>>] {
    const [loading, setLoading] = useState(false);
    const [bodyData, setBodyData] = useState(body);
    const [data, setData] = useState<D>();

    useEffect(() => {
        setLoading(true);
        restRequest<D>(path, bodyData, method).then(setData).finally(() => setLoading(false));
    }, [path, bodyData, method]);

    return [data, loading, setBodyData]
}


export function useLocalStorage<V>(key: string, defaultValue: V): [V, Dispatch<SetStateAction<V>>] {
    const [data, setData] = useState<V>(defaultValue);
    const countRef = useRef(0);
    const skipReloadRef = useRef(false);

    useEffect(() => {
        if (countRef.current > 0 && !skipReloadRef.current) {
            localStorage.setItem(key, JSON.stringify(data));
        }
        ++countRef.current;
        skipReloadRef.current = false;
    }, [key, data]);

    useEffect(() => {
        const jsonStr = localStorage.getItem(key);
        if (jsonStr) {
            try {
                setData(JSON.parse(jsonStr));
                skipReloadRef.current = true;
                return;
            } catch (e) { }
        }

        setData(defaultValue);
    }, [key, JSON.stringify(defaultValue)]);

    return [data, setData];
}


export function useTableSort<D>(defaultField?: keyof D, defaultDir?: number): [{ field?: keyof D, dir: number }, (field: keyof D) => { sorted: "ascending" | "descending" | undefined, onClick: () => void }] {
    const [sort, setSort] = useState<{ field?: keyof D, dir: number }>({ field: defaultField, dir: defaultDir || 0 });

    return [sort, (field: keyof D) => {
        if (field === sort.field) {
            return {
                sorted: [undefined, "ascending", "descending"][sort.dir],
                onClick: () => {
                    const dir = (sort.dir + 1) % 3;
                    setSort(s => ({ ...s, dir: dir }));
                }
            }
        } else {
            return {
                onClick: () => {
                    setSort(s => ({ field, dir: 1 }));
                }
            } as any;
        }
    }]
}